export const APP_KEY = 'app';
export const CLIENT_KEY = 'client';
export const RETURN_URL_KEY = 'returnUrl';

export const KEYS = [
    APP_KEY,
    CLIENT_KEY,
    RETURN_URL_KEY,
];

export const APPS = [
    'geckoform',
    'geckochat',
    'geckomanage',
];

export const ALLOWED_RETURN_URLS = [
    'geckovisit://',
]

export const CLIENT_WEB = 'web';
export const CLIENT_CAPTURE = 'capture';
export const CLIENT_SCAN = 'scan';
export const CLIENT_MOBILE = 'mobile';
export const CLIENT_API = 'api';
export const CLIENT_INTEGRATION = 'integration';

export const CLIENTS = [
    CLIENT_WEB,
    CLIENT_CAPTURE,
    CLIENT_SCAN,
    CLIENT_MOBILE,
    CLIENT_API,
    CLIENT_INTEGRATION,
];

const ClientManager = {
    /**
     * Persist a value to local storage
     *
     * @param string key
     * @param string value
     *
     * @return void
     */
    persist: (key, value) => {
        if (!KEYS.includes(key)) {
            throw new Error('Invalid key');
        }

        window.localStorage.setItem(key, value);
    },

     /**
     * Get a value from local storage
     *
     * @param string key - to retrieve
     *
     * @return void
     */
    get: (key) => {
        return window.localStorage.getItem(key);
    },

     /**
     * Remove all valid keys from local storage
     *
     * @return void
     */
    clear: () => {
        for (var i = 0; i < KEYS.length; i++) {
            window.localStorage.removeItem(KEYS[i]);
        }
    },

     /**
     * Does the key exist in local storage
     *
     * @param string key - to look for
     *
     * @return bool
     */
    hasItem: (key) => {
        if (window.localStorage.getItem(key)) {
            return true;
        }

        return false;
    },

    /**
     * Is the client persisted to storage
     *
     * @return bool
     */
    hasClient: () => {
        return ClientManager.hasItem(CLIENT_KEY);
    },

    /**
     * Is the app persisted to storage
     *
     * @return bool
     */
    hasApp: () => {
        return ClientManager.hasItem(APP_KEY);
    },

    hasReturnUrl: () => {
        return ClientManager.hasItem(RETURN_URL_KEY);
    },

    /**
     * Get or set the client
     *
     * @param string value - set the client
     *
     * @return string - persisted client value
     */
    client: (value = null) => {
        if (value) {
            const prepared_value = value.toLowerCase();

            if (!CLIENTS.includes(prepared_value)) {
                throw new Error('Invalid client');
            }

            ClientManager.persist(CLIENT_KEY, prepared_value);
            return;
        }

        return ClientManager.get(CLIENT_KEY);
    },

    /**
     * Get or set the app
     *
     * @param string value - set the app
     *
     * @return string - persisted app value
     */
    app: (value = null) => {
        if (value) {
            const prepared_value = value.toLowerCase();

            if (!APPS.includes(prepared_value)) {
                throw new Error('Invalid app');
            }

            ClientManager.persist(APP_KEY, prepared_value);
            return;
        }

        return ClientManager.get(APP_KEY);
    }, 

    /**
     * Get or set the return url
     *
     * @param string value - set the return url
     *
     * @return string - persisted return url value
     */
    returnUrl : (value = null) => {
        if (value) {
            const is_allowed = ALLOWED_RETURN_URLS.some((allowed_url_prefix) => {
                return value.startsWith(allowed_url_prefix);
            })

            if (!is_allowed) {
                throw new Error('Invalid return url');
            }

            ClientManager.persist(RETURN_URL_KEY, value);
            return;
        }

        return ClientManager.get(RETURN_URL_KEY);
    },

    toQueryString: () => {
        const client_value = ClientManager.client();
        const app_value = ClientManager.app();
        const return_url_value = ClientManager.returnUrl();

        if (!client_value && !app_value) {
            return '';
        }

        let client, app, separator, return_url = '';

        if (client_value && app_value) {
            separator = '&';
        }

        if (client_value) {
            client = `client=${client_value}`;
        }

        if (client_value) {
            app = `app=${app_value}`;
        }

        if (return_url_value) {
            return_url = `${separator}returnUrl=${return_url_value}`
        }

        return '?' + client + separator + app + return_url;

    },
};

export default ClientManager;
